<template>
  <component
    :is="fieldContainerComponent"
    :field="field"
    :value="value"
    :validation-errors="errors"
    :element-id="elementId"
  >
    <component
      :is="fieldComponent"
      v-model="value"
      :field="field"
      :element-id="elementId"
      :foreign-id="foreignId"
      :disabled="isDisabled"
      :is-manager="isManager"
      :validation-errors.sync="instantErrors"
      :empty-option="true"
      :country="country"
      :in-active-tab="isInActiveTab"
      :enable-markdown="enableMarkdown"
      @uploading="onUploading"
      @input="clearApiErrors"
    />
    <template slot="controls">
      <slot name="controls"></slot>
    </template>
  </component>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex';
import components, { fieldTypes } from '../../../lib/components/Fields';
import AttachmentFieldContainer from './AttachmentFieldContainer';
import ContributorFieldContainer from './ContributorFieldContainer';
import EntryFieldContainer from './EntryFieldContainer';
import fieldStateMixin from './field-state-mixin.js';

export default {
  inject: ['lang'],
  components: {
    AttachmentFieldContainer,
    ContributorFieldContainer,
    EntryFieldContainer,
    ...components
  },
  mixins: [fieldStateMixin],
  props: {
    field: {
      type: Object,
      required: true
    },
    resource: {
      type: Object,
      default: null
    },
    tab: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      instantErrors: []
    };
  },
  computed: {
    isInActiveTab() {
      // eslint-disable-next-line eqeqeq
      return this.selectedTab.id == this.tab.id;
    },
    fieldComponent() {
      return fieldTypes[this.field.type];
    },
    fieldContainerComponent() {
      switch (this.field.resource) {
        case 'Attachments':
          return 'AttachmentFieldContainer';
        case 'Contributors':
          return 'ContributorFieldContainer';
        default:
          return 'EntryFieldContainer';
      }
    },
    elementId() {
      switch (this.field.resource) {
        case 'Attachments':
          return 'Attachments-' + this.resource.fileId;
        case 'Contributors':
          return 'Contributors-' + this.resource.id;
        default:
          return 'Entries';
      }
    },
    errors() {
      return this.instantErrors
        .map(error =>
          this.lang.get('validation.' + error, { attribute: this.field.labelStripped })
        )
        .concat(
          (
            this.errorBag.find(error => this.isMyError(error)) || {
              message: []
            }
          ).message
        );
    },
    apiError() {
      switch (this.field.resource) {
        case 'Attachments':
          return {
            type: 'AttachmentField',
            fieldSlug: this.field.slug,
            fileId: this.resource.fileId
          };
        case 'Contributors':
          return {
            type: 'ContributorField',
            fieldSlug: this.field.slug,
            resourceId: this.resource.id
          };
        case 'Entries':
          return {
            type: 'EntryField',
            fieldSlug: this.field.slug
          };
        default:
          return null;
      }
    },
    isDisabled() {
      return (
          (!this.tabIsVisible(this.tab) && !this.isManager)  ||
        (!this.isManager && !this.field.entrantWriteAccess) ||
        !this.attachedToCategory(this.field) ||
        (this.configurationMode && this.field.dimmed) ||
        (this.configurationMode && this.field.type === 'file') ||
        this.locks.lockedOnSubmission ||
          (!this.hasManagerRole && this.locks.readOnly) ||
        this.locks.seasonNotActive
      );
    },
    enableMarkdown() {
      return this.field.enableMarkdown && this.field.resource === 'Entries';
    },
    ...mapState('entryForm', ['isManager', 'locks', 'hasManagerRole']),
    ...mapState('entryFormApi', ['errorBag']),
    ...mapState('entryFormConfiguration', ['configurationMode']),
    ...mapState('global', ['country']),
    ...mapGetters('entryForm', {
      foreignId: 'entryId',
      selectedTab: 'selectedTab',
      tabIsVisible: 'tabIsVisible',
      attachedToCategory: 'attachedToCategory'
    })
  },
  watch: {
    instantErrors(val) {
      this.storeFieldErrors({
        key: this.resource
          ? 'field-' +
            this.resource +
            '-' +
            this.resource.slug +
            '-' +
            this.field.slug
          : 'field-' + this.field.slug,
        errors: val,
        tab: this.tab.id
      });
    }
  },
  methods: {
    ...mapMutations('entryForm', ['setUploaderStatus', 'storeFieldErrors']),
    ...mapMutations('entryFormApi', ['removeFromErrorBag']),
    onUploading(uploaderId, isUploading) {
      this.setUploaderStatus({ uploaderId, isUploading });
    },
    clearApiErrors() {
      this.removeFromErrorBag(this.apiError);
    },
    isMyError(error) {
      switch (this.field.resource) {
        case 'Attachments':
          return (
            error.type === 'AttachmentField' &&
            error.fieldSlug === this.field.slug &&
            // eslint-disable-next-line eqeqeq
            error.fileId == this.resource.fileId
          );
        case 'Contributors':
          return (
            error.type === 'ContributorField' &&
            error.fieldSlug === this.field.slug &&
            // eslint-disable-next-line eqeqeq
            error.resourceId == this.resource.id
          );
        case 'Entries':
          return (
            error.type === 'EntryField' && error.fieldSlug === this.field.slug
          );
        default:
          return false;
      }
    }
  }
};
</script>
